import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  Popover,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import SearchIcon from "@material-ui/icons/Search";
import CustomSortIcon from "./CustomSortIcon";
import WorkflowNameInput from "./WorkflowNameInput";
import WorkflowDefinitionSearch from "./WorkflowDefinitionSearch";
import Dropdown from "./Dropdown";

const CustomHeaderComponent = React.memo((props) => {
  const {
    columnConfig,
    currentSort,
    currentFilters,
    onSortChanged,
    onFilterChanged,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValue, setFilterValue] = useState(
    currentFilters[columnConfig.field] || ""
  );

  const isSorted = currentSort?.startsWith(columnConfig.field);
  const sortDirection = currentSort?.split(":")[1] || null;
  const hasActiveFilter = Boolean(currentFilters[columnConfig.field]);

  useEffect(() => {
    setFilterValue(currentFilters[columnConfig.field] || "");
  }, [currentFilters, columnConfig.field]);

  const handleSortClick = (e) => {
    if (!columnConfig.sortable) return;
    e.stopPropagation();

    const fieldName = columnConfig.keywordField
      ? `${columnConfig.field}.keyword`
      : columnConfig.field;

    let newSort;
    if (!isSorted) newSort = `${fieldName}:DESC`;
    else if (sortDirection === "DESC") newSort = `${fieldName}:ASC`;
    else newSort = `${fieldName}:DESC`;

    onSortChanged(newSort);
  };

  const handleFilterClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleFilterClose = () => setAnchorEl(null);

  const handleFilterApply = useCallback(() => {
    onFilterChanged(columnConfig.field, filterValue);
    handleFilterClose();
  }, [filterValue, columnConfig.field, onFilterChanged]);

  const handleFilterClear = () => {
    setFilterValue("");
    onFilterChanged(columnConfig.field, "");
    handleFilterClose();
  };

  const getFilterComponent = () => {
    switch (columnConfig.filterType) {
      case "datetime":
        return (
          <TextField
            label="Filter by date"
            type="datetime-local"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        );
      case "select":
        return (
          <FormControl fullWidth>
            <Select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {columnConfig.filterOptions?.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "workflowName":
        return (
          <WorkflowNameInput
            value={Array.isArray(filterValue) ? filterValue : []}
            onChange={(e, newVal) => setFilterValue(newVal || [])}
            placeholder="Filter by Workflow Name"
            fullWidth
          />
        );

      case "workflowDefinitionSearch":
        return (
          <WorkflowDefinitionSearch
            value={filterValue}
            onChange={(newVal) => setFilterValue(newVal)}
          />
        );

      default:
        return (
          <TextField
            label="Filter"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder="Enter filter value"
            fullWidth
          />
        );
    }
  };

  const open = Boolean(anchorEl);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "0 4px",
        marginLeft: "6px",
      }}
    >
      <Typography
        style={{
          fontWeight: 500,
          fontSize: "14px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {columnConfig.headerName}
      </Typography>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginLeft: 8,
        }}
      >
        {columnConfig.filterable && (
          <IconButton
            size="small"
            onClick={handleFilterClick}
            style={{
              color: hasActiveFilter ? "#1976d2" : "#9e9e9e",
              padding: 6,
              borderRadius: "4px",
              transition: "all 0.2s ease",
              backgroundColor: hasActiveFilter
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
            }}
            title={hasActiveFilter ? "Filter applied" : "Add filter"}
          >
            <SearchIcon style={{ fontSize: 16 }} />
          </IconButton>
        )}

        {columnConfig.sortable && (
          <IconButton
            size="small"
            onClick={handleSortClick}
            style={{
              color: isSorted ? "#1976d2" : "#9e9e9e",
              padding: 6,
              borderRadius: "4px",
              transition: "all 0.2s ease",
              backgroundColor: isSorted
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
            }}
            title="Sort"
          >
            {isSorted ? (
              sortDirection === "DESC" ? (
                <ArrowDownwardIcon style={{ fontSize: 14 }} />
              ) : (
                <ArrowUpwardIcon style={{ fontSize: 14 }} />
              )
            ) : (
              <CustomSortIcon size={14} style={{ opacity: 0.7 }} />
            )}
          </IconButton>
        )}
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <div style={{ padding: "16px", minWidth: "250px" }}>
          {getFilterComponent()}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "8px",
              gap: "8px",
            }}
          >
            <Button variant="outlined" size="small" onClick={handleFilterClear}>
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleFilterApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
});

export default CustomHeaderComponent;
